/**
 * Step 1: Welcome & User Signup (with password for real auth)
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';

export default function WelcomeStep({ onComplete }) {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    bn_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const user = await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        charity_bn: formData.bn_number || null,
      });
      onComplete({ ...user, has_bn_number: !!formData.bn_number, bn_number: formData.bn_number });
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-3">
          Welcome to Impact Plexus
        </h2>
        <p className="text-slate-400">
          AI-powered grant discovery for Canadian nonprofits
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="full_name" className="text-white">Full Name *</Label>
          <Input
            id="full_name" name="full_name" type="text" required
            value={formData.full_name} onChange={handleChange}
            className="bg-slate-700 border-slate-600 text-white"
            placeholder="Jane Smith"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-white">Email Address *</Label>
          <Input
            id="email" name="email" type="email" required
            value={formData.email} onChange={handleChange}
            className="bg-slate-700 border-slate-600 text-white"
            placeholder="jane@yourorg.ca"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-white">Password *</Label>
          <Input
            id="password" name="password" type="password" required
            value={formData.password} onChange={handleChange}
            className="bg-slate-700 border-slate-600 text-white"
            placeholder="Min. 8 characters"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="bn_number" className="text-white">
            CRA Business Number <span className="text-slate-500 font-normal">(optional)</span>
          </Label>
          <Input
            id="bn_number" name="bn_number" type="text"
            value={formData.bn_number} onChange={handleChange}
            className="bg-slate-700 border-slate-600 text-white"
            placeholder="123456789RR0001"
            maxLength={15}
          />
          <p className="text-xs text-slate-400">
            Auto-fills your organization details from CRA records
          </p>
        </div>

        <div className="pt-2">
          <Button
            type="submit" disabled={loading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white"
          >
            {loading ? 'Creating Account…' : 'Create Account & Continue'}
          </Button>
        </div>

        <p className="text-xs text-slate-500 text-center">
          Already have an account?{' '}
          <a href="/login" className="text-blue-400 hover:underline">Sign in</a>
        </p>

        <p className="text-xs text-slate-600 text-center">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </form>
    </div>
  );
}
